// itemSkill Tier 値表。docs/adr/0021-itemskill-tier-values.md。
//
// `ItemSkill.values` は 1 channel × Tier 5 段 (`values[tier]` が実効値)。本テーブルは
// sim が実際に消費するチャネルのみを埋める:
//   - proc 発火率: `kind ∈ {trigger, reactive}` のスキル。proc-chance.ts
//     `readBaseProcChance` が `values[tier]` を base 発火率として読む
//   - cdrRate: `Skill.cdrRate` 設定済スキル。cd-layers.ts `readCdrRate` が
//     `values[tier]` を Tier 上書き値として読む (未設定なら `Skill.cdrRate` fallback)
//   - passive stat-boost / shield / regen: Phase 5.x-A2 reducer が
//     `values[tier]` を実効果量として読む。
//
// 数値は全て暫定。Phase 4 sim ベンチで校正する。
import { ITEM_SKILLS, ITEM_SKILL_IDS } from './item-skills.js';
/**
 * proc 発火率の Tier カーブ。全 proc スキル共通の単一カーブ (ADR 0021 §D2)。
 * T1 = 0.05 / T5 = 0.15、Tier 2-4 は準指数カーブ [0, 0.18, 0.4, 0.66, 1.0] で補間。
 * archetype 別の proc 差別化は Phase 4 sim ベンチ校正スコープ。
 */
export const PROC_TIER_CURVE = [0.05, 0.068, 0.09, 0.116, 0.15];
/**
 * cdr の Tier 倍率。`Skill.cdrRate` を base としてこれを乗じる (ADR 0021 §D3)。
 * `1 + 0.8 × [0, 0.18, 0.4, 0.66, 1.0]`。T1 = base ×1.0 (fallback 連続性) /
 * T5 = base ×1.8。
 */
export const CDR_MULT = [1.0, 1.144, 1.32, 1.528, 1.8];
/** Passive stat-boost magnitude. T1 = +5%, T5 = +15%. */
export const STAT_BOOST_TIER_CURVE = [0.05, 0.068, 0.09, 0.116, 0.15];
/** Generic output magnitude multiplier for passive shield / regen base powers. */
export const OUTPUT_MAGNITUDE_MULT = [1.0, 1.3, 1.6, 2.0, 2.5];
/** 小数第 3 位で丸める。 */
function round3(v) {
    return Math.round(v * 1000) / 1000;
}
/**
 * `Skill.cdrRate` base から cdr Tier 値を生成。T1 は丸めず `base` をそのまま使い
 * 「T1 = Skill.cdrRate」(`readCdrRate` fallback との連続性) を実装で保証する。
 */
function cdrTiers(base) {
    return [
        base,
        round3(base * CDR_MULT[1]),
        round3(base * CDR_MULT[2]),
        round3(base * CDR_MULT[3]),
        round3(base * CDR_MULT[4]),
    ];
}
function outputTiers(base) {
    return [
        base,
        round3(base * OUTPUT_MAGNITUDE_MULT[1]),
        round3(base * OUTPUT_MAGNITUDE_MULT[2]),
        round3(base * OUTPUT_MAGNITUDE_MULT[3]),
        round3(base * OUTPUT_MAGNITUDE_MULT[4]),
    ];
}
function firstEffectPower(skill, kind) {
    const effect = skill.effects.find((e) => e.kind === kind);
    return effect?.kind === kind ? effect.power : undefined;
}
function isPassiveStatBoost(skill) {
    return (skill.kind === 'passive' &&
        skill.effects.some((e) => e.kind === 'applyBuff' && e.buffId.startsWith('stat-boost.')));
}
function buildTierValues() {
    const out = {};
    for (const id of ITEM_SKILL_IDS) {
        const skill = ITEM_SKILLS[id];
        if (skill.cdrRate !== undefined) {
            // cdr スキル: cdrRate base から Tier カーブを生成 (proc 判定より優先)。
            out[id] = cdrTiers(skill.cdrRate);
        }
        else if (skill.kind === 'trigger' || skill.kind === 'reactive') {
            // proc スキル: 全 proc 共通カーブ。
            out[id] = PROC_TIER_CURVE;
        }
        else if (isPassiveStatBoost(skill)) {
            out[id] = STAT_BOOST_TIER_CURVE;
        }
        else if (skill.kind === 'passive') {
            const shieldPower = firstEffectPower(skill, 'shield');
            const regenPower = skill.periodTicks !== undefined ? firstEffectPower(skill, 'heal') : undefined;
            if (shieldPower !== undefined) {
                out[id] = outputTiers(shieldPower);
            }
            else if (regenPower !== undefined) {
                out[id] = outputTiers(regenPower);
            }
        }
    }
    return out;
}
/**
 * itemSkill Tier 値表。proc / cdr / passive magnitude reducer が存在するスキルだけ
 * エントリを持つ疎な表。それ以外のスキルはキーを持たない (lookup は `undefined`)。
 */
export const ITEM_SKILL_TIER_VALUES = buildTierValues();
//# sourceMappingURL=item-skill-values.js.map